import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import validateUtils from '../../utils/validate';
import userService from '../../services/userService';
import deliveryOrderService from '../../services/deliveryOrderService';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import SearchableSelect from '../common/SearchableSelect';
import { useTranslation } from 'react-i18next';

const LABOUR_CACHE_KEY = 'start_truck_labour_cache';

const StartTruckLoading = ({ isOpen, orderId, initialValues = null, onClose, onSubmit }) => {
    const { t } = useTranslation();


    const [labours, setLabours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [vehicleNumber, setVehicleNumber] = useState('');
    const [driverContact, setDriverContact] = useState('');
    const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
    const [selectedLabours, setSelectedLabours] = useState([]);
    const [remark, setRemark] = useState('');
    const [internalRemark, setInternalRemark] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [showVehicleTooltip, setShowVehicleTooltip] = useState(false);
    const [orderItems, setOrderItems] = useState([]);
    const [orderDetails, setOrderDetails] = useState({});

    const formStrings = t('deliveryOrdersModule.forms.startTruck', { returnObjects: true });

    // Reset form when modal closes (or prep for open if needed)
    useEffect(() => {
        if (!isOpen) {
            setVehicleNumber('');
            setDriverContact('');
            setEmptyTruckWeight('');
            setRemark('');
            setInternalRemark('');
            setSelectedLabours([]);
            setFieldErrors({});
            setError(null);
            setOrderItems([]);
        }
    }, [isOpen]);

    // Load order details and labours
    useEffect(() => {
        if (!isOpen) return;

        console.log('StartTruckLoading: Modal opened, orderId:', orderId);
        let isMounted = true;

        const loadData = async () => {
            // Safety timeout to prevent infinite loading
            const timeoutId = setTimeout(() => {
                if (isMounted) {
                    console.warn('StartTruckLoading: Safety timeout triggered');
                    setLoading(false);
                    setError("Loading timed out. Please try again.");
                }
            }, 10000);

            setLoading(true);
            setError(null);

            try {
                // 1. Fetch Order Details
                let orderData = initialValues;
                if (!orderData && orderId) {
                    const orderRes = await deliveryOrderService.getDeliverySubOrderById(orderId);
                    if (orderRes.success && orderRes.data) {
                        orderData = orderRes.data;
                    } else {
                        console.error('StartTruckLoading: Failed to fetch order:', orderRes.error);
                        if (isMounted) setError(orderRes.error || formStrings.messages.invalidOrder);
                    }
                }

                if (isMounted && orderData) {
                    setVehicleNumber(orderData.vehicle_number || '');
                    setDriverContact(orderData.driver_contact || '');
                    setEmptyTruckWeight(orderData.empty_truck_weight ? String(orderData.empty_truck_weight) : '');
                    setRemark(orderData.order_remark || orderData.remark || '');
                    setInternalRemark(orderData.internal_remark || '');
                    const ids = Array.isArray(orderData.labour_ids) ? orderData.labour_ids.map(String) : [];
                    setSelectedLabours(ids);
                    setOrderItems(Array.isArray(orderData.items) ? orderData.items : []);
                    setOrderDetails({
                        total_quantity: orderData.total_quantity,
                        unit: orderData.items?.[0]?.unit || '',
                        internal_remark: orderData.internal_remark || '',
                    });
                }

                // 2. Fetch Labours
                console.log('StartTruckLoading: Fetching labours...');
                const cached = sessionStorage.getItem(LABOUR_CACHE_KEY);
                let fetchedLabours = [];
                if (cached) {
                    try {
                        fetchedLabours = JSON.parse(cached);
                    } catch (e) { }
                }

                if (fetchedLabours.length === 0) {
                    const res = await userService.getLabours();
                    console.log('StartTruckLoading: Labours response:', res);
                    if (res.success && Array.isArray(res.data)) {
                        fetchedLabours = res.data.map(u => ({
                            id: u.id,
                            name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User ${u.id}`,
                        }));
                        try { sessionStorage.setItem(LABOUR_CACHE_KEY, JSON.stringify(fetchedLabours)); } catch (e) { }
                    } else {
                        console.warn('StartTruckLoading: Failed to load labours', res.error);
                        // Don't block UI if labours fail
                        if (isMounted) setError(formStrings.messages.loadingUsers);
                    }
                }

                if (isMounted) {
                    setLabours(fetchedLabours);
                }

            } catch (err) {
                console.error('StartTruckLoading: Failed to load data:', err);
                if (isMounted) setError(formStrings.messages.loadingUsers);
            } finally {
                clearTimeout(timeoutId);
                console.log('StartTruckLoading: Finished loading');
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        const timer = setTimeout(() => {
            loadData();
        }, 300);
        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [isOpen, orderId, initialValues, formStrings.messages.loadingUsers, formStrings.messages.invalidOrder]);

    if (!isOpen) return null;


    const handleSubmit = async () => {
        setError(null);

        // Field-level validation
        const errs = {};

        if (!orderId) {
            setError(formStrings.messages.invalidOrder);
            return;
        }

        // Vehicle number: required + format check
        if (!vehicleNumber || String(vehicleNumber).trim() === '') {
            errs.vehicleNumber = formStrings.messages.vehicleRequired;
        } else if (!validateUtils.isValidVehicleNumber(vehicleNumber)) {
            errs.vehicleNumber = `Please enter a valid vehicle number. ${t('deliveryOrdersModule.forms.assignPersonnel.labels.vehicleNumberHint')}`;
        }

        // Driver/mobile contact: use validateUtils helper (strips non-digits internally)
        if (!driverContact || !validateUtils.isValidPhoneNumber(driverContact, { requiredLength: 10 })) {
            errs.driverContact = formStrings.messages.invalidDriverContact;
        }

        if (emptyTruckWeight === null || emptyTruckWeight === undefined || String(emptyTruckWeight).trim() === '' || Number.isNaN(Number(emptyTruckWeight)) || Number(emptyTruckWeight) < 0) {
            errs.emptyTruckWeight = formStrings.messages.invalidEmptyTruckWeight;
        }

        if (!selectedLabours || selectedLabours.length === 0) {
            errs.selectedLabours = formStrings.messages.selectAtLeastOneLabour;
        }

        if (Object.keys(errs).length > 0) {
            setFieldErrors(errs);
            return;
        }

        setFieldErrors({});
        setSubmitting(true);

        try {
            const payload = {
                vehicle_number: vehicleNumber || undefined,
                driver_contact: driverContact || undefined,
                empty_truck_weight: emptyTruckWeight ? Number(emptyTruckWeight) : undefined,
                labour_ids: selectedLabours.map(id => Number(id)),
                order_remark: remark || undefined,
                internal_remark: internalRemark || undefined,
            };

            if (onSubmit) {
                const result = await onSubmit(orderId, payload);
                if (result && result.success) {
                    onClose && onClose();
                } else {
                    setError(result?.error || formStrings.messages.startFailed);
                }
            } else {
                setError("No submit handler provided");
            }
        } catch (err) {
            console.error('Submit error:', err);
            setError(err?.message || formStrings.messages.startFailed);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        >
            <div
                className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
                style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-6 border-b"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {formStrings.title}
                        </h2>
                        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {formStrings.subtitle}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors hover:opacity-80"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex-1 overflow-y-auto">

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div
                                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                                style={{
                                    borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5),
                                }}
                            />
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {formStrings.messages.loadingUsers}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Form Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Vehicle Number */}
                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL }}
                                    >
                                        {formStrings.labels.vehicleNumber} <span>*</span>
                                        <span
                                            className="ml-2 cursor-pointer relative inline-block"
                                            onMouseEnter={() => setShowVehicleTooltip(true)}
                                            onMouseLeave={() => setShowVehicleTooltip(false)}
                                            style={{ color: UI_COLORS.TEXT.MUTED }}
                                        >
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            {showVehicleTooltip && (
                                                <span
                                                    className="absolute left-0 top-6 z-50 px-3 py-2 text-xs font-normal rounded-lg shadow-lg"
                                                    style={{
                                                        backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                                                        color: UI_COLORS.TEXT.PRIMARY,
                                                        border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                                                        minWidth: '150px',
                                                    }}
                                                >
                                                    <div>Accepted formats:</div>
                                                    <div className="mt-1">• KA-53-1234</div>
                                                    <div>• RJ14CA4241</div>
                                                    <div>• RJ 14 CA 1424</div>
                                                    <div>• RJ-14-CA-2414</div>
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        value={vehicleNumber}
                                        onChange={(e) => setVehicleNumber(e.target.value)}
                                        onBlur={() => { if (fieldErrors.vehicleNumber) { setFieldErrors(prev => { const p = { ...prev }; delete p.vehicleNumber; return p; }); } }}
                                        className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: fieldErrors.vehicleNumber ? UI_COLORS.FORMS.INPUT.error : UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={formStrings.placeholders?.vehicleNumber}
                                    />
                                    {fieldErrors.vehicleNumber && (
                                        <div className="text-xs mt-2" style={{ color: UI_COLORS.STATUS.ERROR }} role="alert">{fieldErrors.vehicleNumber}</div>
                                    )}
                                </div>

                                {/* Driver Contact */}
                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL }}
                                    >
                                        {formStrings.labels.driverContact} <span>*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        value={driverContact}
                                        onChange={(e) => setDriverContact(validateUtils.formatNumericInput(e.target.value, { maxLength: 10 }))}
                                        maxLength={10}
                                        onBlur={() => { if (fieldErrors.driverContact) { setFieldErrors(prev => { const p = { ...prev }; delete p.driverContact; return p; }); } }}
                                        className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: fieldErrors.driverContact ? UI_COLORS.FORMS.INPUT.error : UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={formStrings.placeholders?.driverContact}
                                    />
                                    {fieldErrors.driverContact && (
                                        <div className="text-xs mt-2" style={{ color: UI_COLORS.STATUS.ERROR }} role="alert">{fieldErrors.driverContact}</div>
                                    )}
                                </div>

                                {/* Empty Truck Weight */}
                                <div>
                                    <label
                                        className="block text-sm font-semibold mb-2"
                                        style={{ color: UI_COLORS.FORMS.LABEL }}
                                    >
                                        {formStrings.labels.emptyTruckWeight} <span>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={emptyTruckWeight}
                                        onChange={(e) => setEmptyTruckWeight(e.target.value)}
                                        onBlur={() => { if (fieldErrors.emptyTruckWeight) { setFieldErrors(prev => { const p = { ...prev }; delete p.emptyTruckWeight; return p; }); } }}
                                        className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: fieldErrors.emptyTruckWeight ? UI_COLORS.FORMS.INPUT.error : UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={formStrings.placeholders?.emptyTruckWeight}
                                        step="0.01"
                                        min="0"
                                    />
                                    {fieldErrors.emptyTruckWeight && (
                                        <div className="text-xs mt-2" style={{ color: UI_COLORS.STATUS.ERROR }} role="alert">{fieldErrors.emptyTruckWeight}</div>
                                    )}
                                </div>

                                {/* Labours Selection */}
                                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 rounded-xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.MAIN, 0.5) }}>
                                    {/* Left Side: Dropdown */}
                                    <div>
                                        <label
                                            className="block text-sm font-semibold mb-2"
                                            style={{ color: UI_COLORS.FORMS.LABEL }}
                                        >
                                            {formStrings.labels.labours}
                                        </label>
                                        <div className="relative">
                                            <SearchableSelect
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        const val = e.target.value;
                                                        setSelectedLabours(prev => {
                                                            const current = prev.map(String);
                                                            return current.includes(val) ? current : [...current, val];
                                                        });
                                                        // Clear error when labour is selected
                                                        if (fieldErrors.selectedLabours) {
                                                            setFieldErrors(prev => {
                                                                const p = { ...prev };
                                                                delete p.selectedLabours;
                                                                return p;
                                                            });
                                                        }
                                                    }
                                                }}
                                                options={labours
                                                    .filter(l => !selectedLabours.includes(String(l.id)))
                                                    .map(labour => ({ value: labour.id, label: labour.name }))
                                                }
                                                placeholder={formStrings.placeholders.selectLabour}
                                                className="w-full"
                                            />
                                        </div>
                                        <p className="text-xs mt-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                            {t('common.forms.startTruck.messages.selectLabourHelper')}
                                        </p>
                                    </div>

                                    {/* Right Side: Selected Labours */}
                                    <div>
                                        <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                            {t('common.formFields.labels.selectedLabours', { n: String(selectedLabours.length) })}
                                        </label>
                                        <div className="min-h-[100px] p-3 rounded-xl border-2 bg-white" style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}>
                                            {selectedLabours.length === 0 ? (
                                                <p className="text-sm text-center py-8 italic" style={{ color: UI_COLORS.TEXT.MUTED }}>{formStrings.messages.noLaboursSelected}</p>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {selectedLabours.map(id => {
                                                        const labour = labours.find(l => String(l.id) === String(id));
                                                        if (!labour) return null;
                                                        return (
                                                            <div
                                                                key={id}
                                                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium shadow-sm"
                                                                style={{
                                                                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1),
                                                                    color: '#000000',
                                                                    border: '1px solid #000000'
                                                                }}
                                                            >
                                                                <span>{labour.name}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setSelectedLabours(prev => prev.filter(pid => String(pid) !== String(id)))}
                                                                    className="hover:text-red-500 transition-colors"
                                                                    title={t('common.actions.remove')}
                                                                >
                                                                    <FontAwesomeIcon icon={faTimes} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        {fieldErrors.selectedLabours && (
                                            <div className="text-sm font-semibold mt-2" style={{ color: UI_COLORS.STATUS.ERROR }} role="alert">{fieldErrors.selectedLabours}</div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* Order Items - Initial Pieces */}
                            {orderItems.length > 0 && (
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        {t('common.formFields.labels.itemsAndInitialPieces')}
                                    </label>
                                    <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.MAIN, 0.6) }}>
                                                    <th className="px-4 py-2 text-left font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.product')}</th>
                                                    <th className="px-4 py-2 text-center font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.qty')}</th>
                                                    <th className="px-4 py-2 text-center font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.pieces')}</th>
                                                    <th className="px-4 py-2 text-center font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.loadBy')}</th>
                                                    <th className="px-4 py-2 text-left font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('common.formFields.labels.remarks')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderItems.map((item, idx) => {
                                                    const itemRemark = item.remark || item.remarks || item.note || item.notes || '';
                                                    const hasRemark = Boolean(itemRemark && itemRemark.trim());
                                                    return (
                                                        <tr
                                                            key={idx}
                                                            style={{
                                                                borderTop: idx > 0 ? `1px solid ${UI_COLORS.BORDERS.LIGHT}` : undefined,
                                                                backgroundColor: hasRemark ? 'rgba(251, 191, 36, 0.06)' : undefined,
                                                                borderLeft: hasRemark ? '3px solid #f59e0b' : '3px solid transparent',
                                                            }}
                                                        >
                                                            <td className="px-4 py-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                {item.product_alias || item.size_name || item.product_name || '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-center" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                                {item.quantity ? `${item.quantity} ${item.unit || item.units || ''}` : '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-center font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                                {(item.quantity_in_pieces ?? item.pieces ?? item.actual_pieces) !== null && (item.quantity_in_pieces ?? item.pieces ?? item.actual_pieces) !== undefined ? `${item.quantity_in_pieces ?? item.pieces ?? item.actual_pieces} pcs` : '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-center">
                                                                {item.load_by_pieces ? (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('common.formFields.labels.pieces')}</span>
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">{t('common.units.kgOrMtr')}</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                {hasRemark ? (
                                                                    <div
                                                                        style={{
                                                                            display: '-webkit-box',
                                                                            WebkitLineClamp: 3,
                                                                            WebkitBoxOrient: 'vertical',
                                                                            overflow: 'hidden',
                                                                            whiteSpace: 'pre-wrap',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '0.82rem',
                                                                            fontWeight: 500,
                                                                            backgroundColor: 'rgba(251, 191, 36, 0.15)',
                                                                            color: '#92400e',
                                                                            border: '1px solid rgba(251, 191, 36, 0.4)',
                                                                            maxWidth: '250px'
                                                                        }}
                                                                    >
                                                                        <span className="font-bold opacity-70 uppercase text-[10px] mr-1">{t('common.formFields.labels.remark')}:</span> {itemRemark}
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: UI_COLORS.TEXT.MUTED }}>—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" >
                                                {formStrings.labels.totalQuantity}
                                            </p>
                                            <p className="font-semibold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                {orderDetails.total_quantity || 0} {orderDetails.unit || ''}
                                            </p>
                                        </div>

                                        {remark && (
                                            <div>
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400" >
                                                    {t('common.formFields.labels.orderRemark')}
                                                </p>
                                                <div
                                                    className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 mt-1"
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap', width: 'fit-content', minWidth: '150px' }}
                                                >
                                                    {remark}
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-span-2">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{t('common.formFields.labels.internalRemarks')}</p>
                                            <textarea
                                                value={internalRemark}
                                                onChange={(e) => setInternalRemark(e.target.value)}
                                                className="w-full px-3 py-2 rounded-xl border-2 resize-none text-sm"
                                                rows="2"
                                                placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                                style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Remark Textarea */}
                            <div>
                                <label
                                    className="block text-sm font-semibold mb-2"
                                    style={{ color: UI_COLORS.FORMS.LABEL }}
                                >
                                    {formStrings.labels.remark}
                                </label>
                                <textarea
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none resize-none"
                                    style={{
                                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                        borderColor: UI_COLORS.FORMS.INPUT.border,
                                        color: UI_COLORS.FORMS.INPUT.text,
                                    }}
                                    placeholder={t('common.formFields.placeholders.remarkExample')}
                                    rows="3"
                                />
                            </div>

                            {/* Error Alert */}
                            {error && (
                                <div
                                    className="p-4 rounded-xl text-sm font-medium"
                                    style={{
                                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                                        color: UI_COLORS.STATUS.ERROR,
                                    }}
                                >
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-4 p-6 border-t"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-medium transition-colors"
                        disabled={submitting}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                            color: UI_COLORS.BUTTONS.SECONDARY.text,
                            opacity: submitting ? 0.6 : 1,
                        }}
                    >
                        {formStrings.buttons.cancel}
                    </button>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={submitting || loading}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                            color: UI_COLORS.BUTTONS.PRIMARY.text,
                        }}
                    >
                        <span className="inline-flex items-center gap-2">
                            {submitting && (
                                <span className="animate-spin">
                                    <div
                                        className="h-4 w-4 rounded-full border-2 border-b-transparent"
                                        style={{
                                            borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6),
                                        }}
                                    />
                                </span>
                            )}
                            {!submitting && <FontAwesomeIcon icon={faPlay} />}
                            <span>
                                {submitting
                                    ? formStrings.buttons.starting
                                    : formStrings.buttons.start}
                            </span>
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StartTruckLoading;
